import React, { useState, useEffect, useCallback, useMemo, Suspense } from "react";
import { useHistory } from "react-router-dom";
import PortletConfigService, { Portlet, PortletChild, Role, ILearnPortlet, PortletIcon } from "../../_services/PortletConfigService";
import PortletService from "../../_services/PortletService";
import ApiService from "../../_services/ApiService";
import IconDisplay from "../../components/IconDisplay";
import "./portlet-config.css";

// Heavy (loads the full lucide icon map) — lazy so it only loads when opened.
const LucideIconPicker = React.lazy(() => import("../../components/LucideIconPicker"));

// Gradient options for portlets
const GRADIENT_OPTIONS = [
  { label: "Purple", value: "var(--gradient-brand)" },
  { label: "Blue", value: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" },
  { label: "Green", value: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)" },
  { label: "Orange", value: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" },
  { label: "Sunset", value: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" },
  { label: "Ocean", value: "var(--gradient-brand)" },
  { label: "Indigo", value: "var(--gradient-brand)" },
];

// Emoji options for quick selection
const EMOJI_OPTIONS = ["📊", "📈", "🏫", "📋", "⚙️", "👥", "📅", "🎓", "📚", "🔗", "📁", "🔧", "🚀", "💰", "✅", "📝", "🚪", "📉", "📄", "🔍", "🧑‍🎓", "🗓️", "📦", "🎬", "💡", "🧪", "📖", "🎯", "📐", "🖥️", "🧩", "📘", "✏️"];

// Preset colors for Lucide icon tinting. "Default" clears the color so the icon
// inherits currentColor (the dark theme text color).
const ICON_COLOR_OPTIONS = ["#ffffff", "#1e293b", "#ef4444", "#f59e0b", "#eab308", "#10b981", "#3b82f6", "#6366f1", "#a855f7", "#ec4899"];

/**
 * Colour picker shown only for Lucide icons (emoji/image/key ignore colour).
 * `onChange(undefined)` resets to the default inherited colour.
 */
const IconColorPicker = ({
  icon,
  onChange,
}: {
  icon: PortletIcon | string | undefined;
  onChange: (color: string | undefined) => void;
}) => {
  const isLucide = typeof icon !== "string" && icon?.type === "lucide" && !!icon.value;
  if (!isLucide) return null;
  const current = (icon as PortletIcon).color;
  return (
    <div className="icon-color-row">
      <span className="icon-color-label">Icon color</span>
      <div className="icon-color-swatches">
        {ICON_COLOR_OPTIONS.map((c) => (
          <button
            key={c}
            type="button"
            className={`icon-color-swatch ${current && current.toLowerCase() === c ? "selected" : ""}`}
            style={{ background: c }}
            onClick={() => onChange(c)}
            title={c}
          />
        ))}
        <input
          type="color"
          className="icon-color-custom"
          value={current || "#1e293b"}
          onChange={(e) => onChange(e.target.value)}
          title="Custom color"
        />
        <button type="button" className="icon-color-reset" onClick={() => onChange(undefined)}>
          Default
        </button>
      </div>
    </div>
  );
};

// Applies a colour change onto an existing PortletIcon, dropping the key when reset.
const withIconColor = (icon: PortletIcon | string | undefined, color: string | undefined): PortletIcon => {
  const base: PortletIcon = typeof icon === "string" || !icon ? { type: "lucide" } : { ...icon };
  if (color) base.color = color;
  else delete base.color;
  return base;
};

// Role selector component (defined outside to avoid re-mount on parent re-render)
const RoleSelector = ({ roleIds, onChange, availableRoles }: { roleIds: number[]; onChange: (ids: number[]) => void; availableRoles: Role[] }) => {
  const [roleSearch, setRoleSearch] = useState("");

  const filteredRoles = useMemo(() => {
    if (!roleSearch.trim()) return availableRoles;
    const q = roleSearch.toLowerCase();
    return availableRoles.filter((r) => r.name?.toLowerCase().includes(q));
  }, [roleSearch, availableRoles]);

  const toggleRole = (roleId: number) => {
    if (roleIds.includes(roleId)) {
      onChange(roleIds.filter((id) => id !== roleId));
    } else {
      onChange([...roleIds, roleId]);
    }
  };

  const getRoleName = (id: number) => availableRoles.find((r) => r.id === id)?.name || `Role #${id}`;

  return (
    <div className="role-selector">
      {roleIds.length > 0 && (
        <div className="role-chips">
          {roleIds.map((id) => (
            <span key={id} className="role-chip">
              {getRoleName(id)}
              <button type="button" onClick={() => toggleRole(id)}>&times;</button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        className="role-search"
        placeholder="Search roles..."
        value={roleSearch}
        onChange={(e) => setRoleSearch(e.target.value)}
      />
      <div className="role-list">
        {filteredRoles.map((role) => (
          <label key={role.id} className="role-list-item">
            <input
              type="checkbox"
              checked={roleIds.includes(role.id)}
              onChange={() => toggleRole(role.id)}
            />
            {role.name}
          </label>
        ))}
        {filteredRoles.length === 0 && (
          <div className="role-list-empty">No roles found</div>
        )}
      </div>
    </div>
  );
};

// Children editor for group portlets (defined outside to avoid re-mount on parent re-render)
const ChildrenEditor = ({ children, onChange }: { children: PortletChild[]; onChange: (c: PortletChild[]) => void }) => {
  const addChild = () => {
    const id = `child-${Date.now().toString(36)}`;
    onChange([...children, { id, title: "", url: "" }]);
  };

  const updateChild = (index: number, field: keyof PortletChild, value: any) => {
    const updated = children.map((c, i) => (i === index ? { ...c, [field]: value } : c));
    onChange(updated);
  };

  const removeChild = (index: number) => {
    onChange(children.filter((_, i) => i !== index));
  };

  return (
    <div className="children-editor">
      {children.map((child, index) => (
        <div key={child.id} className="child-item">
          <div className="child-item-header">
            <span className="child-item-number">{index + 1}</span>
            <button
              type="button"
              className="child-item-remove"
              onClick={() => removeChild(index)}
              title="Remove"
            >
              &times;
            </button>
          </div>
          <input
            type="text"
            placeholder="Title"
            value={child.title}
            onChange={(e) => updateChild(index, "title", e.target.value)}
          />
          <input
            type="text"
            placeholder="URL or route (e.g. /rubrics)"
            value={child.url}
            onChange={(e) => updateChild(index, "url", e.target.value)}
          />
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={child.openInNewTab || false}
              onChange={(e) => updateChild(index, "openInNewTab", e.target.checked)}
            />
            Open in new tab
          </label>
        </div>
      ))}
      <button type="button" className="btn-add-child" onClick={addChild}>
        + Add Item
      </button>
    </div>
  );
};

// ── Params editor (key/value rows with per-row type) ────────────────────────
// Used only by iLearn portlets — iTeach config is untouched.
type ParamValueType = "text" | "number" | "boolean" | "json";

const detectParamType = (v: unknown): ParamValueType => {
  if (typeof v === "boolean") return "boolean";
  if (typeof v === "number") return "number";
  if (v !== null && typeof v === "object") return "json";
  return "text";
};

const ParamsEditor = ({
  params,
  onChange,
}: {
  params: Record<string, any> | undefined;
  onChange: (next: Record<string, any>) => void;
}) => {
  // Local row state — preserves key edits in progress even when the key
  // is empty (controlled object would otherwise lose the row).
  const [rows, setRows] = useState<Array<{ key: string; value: any; type: ParamValueType; raw: string }>>(
    () => {
      const entries = Object.entries(params || {});
      return entries.map(([k, v]) => {
        const t = detectParamType(v);
        return {
          key: k,
          value: v,
          type: t,
          raw: t === "json" ? JSON.stringify(v, null, 2) : String(v ?? ""),
        };
      });
    },
  );

  const commit = (next: typeof rows) => {
    setRows(next);
    const out: Record<string, any> = {};
    for (const r of next) {
      if (!r.key.trim()) continue;
      if (r.type === "json") {
        try {
          out[r.key] = r.raw.trim() ? JSON.parse(r.raw) : null;
        } catch {
          out[r.key] = r.raw; // keep raw text until parseable
        }
      } else if (r.type === "number") {
        const n = Number(r.raw);
        out[r.key] = Number.isFinite(n) ? n : r.raw;
      } else if (r.type === "boolean") {
        out[r.key] = r.raw === "true";
      } else {
        out[r.key] = r.raw;
      }
    }
    onChange(out);
  };

  const updateRow = (i: number, patch: Partial<(typeof rows)[number]>) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    commit(next);
  };

  const removeRow = (i: number) => {
    commit(rows.filter((_, idx) => idx !== i));
  };

  const addRow = () => {
    commit([...rows, { key: "", value: "", type: "text", raw: "" }]);
  };

  return (
    <div className="children-editor">
      {rows.map((row, i) => (
        <div key={i} className="child-item" style={{ display: "block" }}>
          <div className="child-item-header">
            <span className="child-item-number">{i + 1}</span>
            <button
              type="button"
              className="child-item-remove"
              onClick={() => removeRow(i)}
              title="Remove"
            >
              &times;
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 6 }}>
            <input
              type="text"
              placeholder="Key (e.g. resourceTypeId)"
              value={row.key}
              onChange={(e) => updateRow(i, { key: e.target.value })}
            />
            <select
              value={row.type}
              onChange={(e) => {
                const nextType = e.target.value as ParamValueType;
                let raw = row.raw;
                if (nextType === "boolean" && raw !== "true" && raw !== "false") raw = "false";
                if (nextType === "json" && !raw.trim()) raw = "[]";
                updateRow(i, { type: nextType, raw });
              }}
            >
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="boolean">Boolean</option>
              <option value="json">JSON</option>
            </select>
          </div>
          {row.type === "boolean" ? (
            <select
              value={row.raw || "false"}
              onChange={(e) => updateRow(i, { raw: e.target.value })}
              style={{ marginTop: 6 }}
            >
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          ) : row.type === "json" ? (
            <textarea
              value={row.raw}
              onChange={(e) => updateRow(i, { raw: e.target.value })}
              rows={3}
              placeholder='[] or {"key": "value"}'
              spellCheck={false}
              style={{ marginTop: 6, fontFamily: "monospace", fontSize: 12 }}
            />
          ) : (
            <input
              type="text"
              placeholder={row.type === "number" ? "e.g. 15" : "Value"}
              value={row.raw}
              onChange={(e) => updateRow(i, { raw: e.target.value })}
              style={{ marginTop: 6 }}
            />
          )}
        </div>
      ))}
      <button type="button" className="btn-add-child" onClick={addRow}>
        + Add Param
      </button>
    </div>
  );
};

const PortletConfig: React.FC = () => {
  const history = useHistory();
  // State
  const [portlets, setPortlets] = useState<Portlet[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<"iteach" | "ilearn">("iteach");

  // iLearn state
  const [ilearnPortlets, setIlearnPortlets] = useState<ILearnPortlet[]>([]);
  const [ilearnCategories, setIlearnCategories] = useState<string[]>([]);
  const [ilearnLoading, setIlearnLoading] = useState(false);
  const [ilearnLoaded, setIlearnLoaded] = useState(false);
  const [selectedIlearnPortlet, setSelectedIlearnPortlet] = useState<ILearnPortlet | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // UI state
  const [selectedPortlet, setSelectedPortlet] = useState<Portlet | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addPortletType, setAddPortletType] = useState<"link" | "clone" | "widget" | "group" | null>(null);
  const [draggedPortlet, setDraggedPortlet] = useState<string | null>(null);

  // Lucide icon picker — tracks which of the four icon editors opened it.
  const [lucidePicker, setLucidePicker] = useState<
    null | { target: "iteachEdit" | "iteachAdd" | "ilearnEdit" | "ilearnAdd" }
  >(null);

  const applyLucide = (name: string) => {
    const icon: PortletIcon = { type: "lucide", value: name };
    switch (lucidePicker?.target) {
      case "iteachEdit":
        handleUpdatePortlet("icon", icon);
        break;
      case "iteachAdd":
        setNewPortlet((prev) => ({ ...prev, icon }));
        break;
      case "ilearnEdit":
        handleILearnUpdatePortlet("icon", icon);
        break;
      case "ilearnAdd":
        setNewIlearnPortlet((prev) => ({ ...prev, icon }));
        break;
    }
    setLucidePicker(null);
  };

  // A reusable "Browse Lucide icons" row shown under each emoji grid.
  const LucideBrowseRow = ({
    icon,
    target,
  }: {
    icon: PortletIcon | string | undefined;
    target: "iteachEdit" | "iteachAdd" | "ilearnEdit" | "ilearnAdd";
  }) => {
    const isLucide = typeof icon !== "string" && icon?.type === "lucide" && !!icon.value;
    return (
      <div className="lucide-browse-row">
        <button type="button" className="btn-lucide-browse" onClick={() => setLucidePicker({ target })}>
          🎨 Browse Icon Library
        </button>
        {isLucide && (
          <span className="lucide-current">
            <IconDisplay icon={icon} size={18} />
            {(icon as PortletIcon).value}
          </span>
        )}
      </div>
    );
  };

  // Roles
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);

  // System utilities
  const [refreshingCache, setRefreshingCache] = useState(false);
  const [cacheMessage, setCacheMessage] = useState<string | null>(null);

  const handleRefreshPlaygroundCache = async () => {
    setRefreshingCache(true);
    setCacheMessage(null);
    try {
      const res: any = await ApiService.refreshPlaygroundCache();
      setCacheMessage(`Cache refreshed (${res.data?.subjects || 0} subjects, built ${new Date(res.data?.builtAt).toLocaleTimeString()})`);
    } catch (err) {
      setCacheMessage("Failed to refresh playground cache");
    } finally {
      setRefreshingCache(false);
    }
  };

  // JSON edit mode
  const [jsonEditMode, setJsonEditMode] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);

  // New portlet form state
  const [newPortlet, setNewPortlet] = useState<Partial<Portlet>>({
    type: "link",
    title: "",
    description: "",
    icon: { type: "emoji", value: "🔗" },
    visible: true,
    roleIds: [],
    category: "Custom",
  });

  // New iLearn portlet form state
  const [newIlearnPortlet, setNewIlearnPortlet] = useState<Partial<ILearnPortlet>>({
    type: "standard",
    title: "",
    description: "",
    icon: { type: "emoji", value: "📄" },
    visible: true,
    category: "Learn",
    roleIds: [],
    isTile: true,
    isMenu: false,
    enableForEnrollment: true,
    enableForParent: false,
    enableForEnquiry: false,
  });

  // Load portlet configuration and roles
  useEffect(() => {
    loadConfig();
    loadRoles();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await PortletConfigService.getPortletConfig();
      if (response.success && response.data) {
        setPortlets(response.data.portlets);
        setCategories(response.data.categories);
      } else {
        setError(response.error || "Failed to load configuration");
      }
    } catch (err) {
      setError("Failed to load portlet configuration");
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await PortletConfigService.getRoles();
      if (response.success && response.data) {
        setAvailableRoles(response.data);
      }
    } catch (err) {
      console.error("Failed to load roles:", err);
    }
  };

  // Load iLearn config lazily on tab switch
  useEffect(() => {
    if (activeTab === "ilearn" && !ilearnLoaded && !ilearnLoading) {
      loadILearnConfig();
    }
  }, [activeTab]);

  const loadILearnConfig = async () => {
    setIlearnLoading(true);
    setError(null);
    try {
      const response = await PortletConfigService.getILearnPortletConfig();
      if (response.success && response.data) {
        setIlearnPortlets(response.data.portlets);
        setIlearnCategories(response.data.categories);
        setIlearnLoaded(true);
      } else {
        setError(response.error || "Failed to load iLearn configuration");
      }
    } catch (err) {
      setError("Failed to load iLearn portlet configuration");
    } finally {
      setIlearnLoading(false);
    }
  };

  // Save configuration
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await PortletConfigService.updatePortletConfig(portlets);
      if (response.success) {
        setSuccessMessage("Configuration saved successfully");
      } else {
        setError(response.error || "Failed to save configuration");
      }
    } catch (err) {
      setError("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  // Toggle visibility
  const handleToggleVisibility = (portletId: string) => {
    setPortlets((prev) =>
      prev.map((p) => (p.id === portletId ? { ...p, visible: !p.visible } : p))
    );
  };

  // Delete portlet
  const handleDeletePortlet = async (portletId: string) => {
    if (!window.confirm("Are you sure you want to delete this portlet?")) return;

    try {
      const response = await PortletConfigService.deletePortlet(portletId);
      if (response.success) {
        setPortlets((prev) => prev.filter((p) => p.id !== portletId));
        setSelectedPortlet(null);
        setSuccessMessage("Portlet deleted successfully");
      } else {
        setError(response.error || "Failed to delete portlet");
      }
    } catch (err) {
      setError("Failed to delete portlet");
    }
  };

  // Add new portlet
  const handleAddPortlet = async () => {
    if (!newPortlet.title || !newPortlet.type) {
      setError("Title and type are required");
      return;
    }

    const portlet: Partial<Portlet> = {
      ...newPortlet,
      id: PortletConfigService.generatePortletId(newPortlet.type!, newPortlet.title!),
      order: portlets.length,
    };

    try {
      const response = await PortletConfigService.addPortlet(portlet);
      if (response.success && response.data) {
        setPortlets((prev) => [...prev, response.data!]);
        setShowAddModal(false);
        setAddPortletType(null);
        setNewPortlet({
          type: "link",
          title: "",
          description: "",
          icon: { type: "emoji", value: "🔗" },
          visible: true,
          roleIds: [],
          category: "Custom",
        });
        setSuccessMessage("Portlet added successfully");
      } else {
        setError(response.error || "Failed to add portlet");
      }
    } catch (err) {
      setError("Failed to add portlet");
    }
  };

  // Update selected portlet
  const handleUpdatePortlet = (field: keyof Portlet, value: any) => {
    if (!selectedPortlet) return;

    const updated = { ...selectedPortlet, [field]: value };
    setSelectedPortlet(updated);
    setPortlets((prev) =>
      prev.map((p) => (p.id === selectedPortlet.id ? updated : p))
    );
  };

  // JSON edit mode handlers
  const toggleJsonMode = () => {
    if (!jsonEditMode && selectedPortlet) {
      setJsonText(JSON.stringify(selectedPortlet, null, 2));
      setJsonError(null);
    }
    setJsonEditMode(!jsonEditMode);
  };

  const handleJsonSave = async () => {
    try {
      const parsed = JSON.parse(jsonText) as Portlet;
      if (!parsed.id || !parsed.title) {
        setJsonError("Invalid portlet: missing required fields (id, title)");
        return;
      }

      // Update local state
      setSelectedPortlet(parsed);
      const updatedPortlets = portlets.map((p) => (p.id === parsed.id ? parsed : p));
      setPortlets(updatedPortlets);
      setJsonError(null);
      setJsonEditMode(false);

      // Also save to server
      setSaving(true);
      try {
        const response = await PortletConfigService.updatePortletConfig(updatedPortlets);
        if (response.success) {
          setSuccessMessage("Configuration saved successfully");
        } else {
          setError(response.error || "Failed to save configuration");
        }
      } catch (err) {
        setError("Failed to save configuration");
      } finally {
        setSaving(false);
      }
    } catch (e) {
      setJsonError("Invalid JSON format");
    }
  };

  // Drag and drop handlers
  const handleDragStart = (portletId: string) => {
    setDraggedPortlet(portletId);
  };

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedPortlet || draggedPortlet === targetId) return;
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedPortlet || draggedPortlet === targetId) return;

    const fromIndex = portlets.findIndex((p) => p.id === draggedPortlet);
    const toIndex = portlets.findIndex((p) => p.id === targetId);

    if (fromIndex === -1 || toIndex === -1) return;

    const reordered = [...portlets];
    const [removed] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, removed);

    // Update order values
    const updatedPortlets = reordered.map((p, index) => ({
      ...p,
      order: index,
    }));

    setPortlets(updatedPortlets);
    setDraggedPortlet(null);
  };

  // ── iLearn handlers ──

  const handleILearnSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await PortletConfigService.updateILearnPortletConfig(ilearnPortlets);
      if (response.success) {
        setSuccessMessage("iLearn configuration saved successfully");
      } else {
        setError(response.error || "Failed to save iLearn configuration");
      }
    } catch (err) {
      setError("Failed to save iLearn configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleILearnToggleVisibility = (portletId: string) => {
    setIlearnPortlets((prev) =>
      prev.map((p) => (p.id === portletId ? { ...p, visible: !p.visible } : p))
    );
  };

  const handleILearnDeletePortlet = async (portletId: string) => {
    try {
      const response = await PortletConfigService.deleteILearnPortlet(portletId);
      if (response.success) {
        setIlearnPortlets((prev) => prev.filter((p) => p.id !== portletId));
        setSelectedIlearnPortlet(null);
        setShowDeleteConfirm(null);
        setSuccessMessage("iLearn portlet deleted successfully");
      } else {
        setError(response.error || "Failed to delete iLearn portlet");
      }
    } catch (err) {
      setError("Failed to delete iLearn portlet");
    }
  };

  const handleILearnUpdatePortlet = (field: keyof ILearnPortlet, value: any) => {
    if (!selectedIlearnPortlet) return;
    const updated = { ...selectedIlearnPortlet, [field]: value };
    setSelectedIlearnPortlet(updated);
    setIlearnPortlets((prev) =>
      prev.map((p) => (p.id === selectedIlearnPortlet.id ? updated : p))
    );
  };

  const handleILearnAddPortlet = async () => {
    if (!newIlearnPortlet.title) {
      setError("Title is required");
      return;
    }

    const type = newIlearnPortlet.type || "standard";
    const portlet: Partial<ILearnPortlet> = {
      ...newIlearnPortlet,
      type,
      id: PortletConfigService.generatePortletId(type, newIlearnPortlet.title!),
      order: ilearnPortlets.length,
    };

    try {
      const response = await PortletConfigService.addILearnPortlet(portlet);
      if (response.success && response.data) {
        setIlearnPortlets((prev) => [...prev, response.data!]);
        setShowAddModal(false);
        setAddPortletType(null);
        setNewIlearnPortlet({
          type: "standard",
          title: "",
          description: "",
          icon: { type: "emoji", value: "📄" },
          visible: true,
          category: "Learn",
          roleIds: [],
          isTile: true,
          isMenu: false,
          enableForEnrollment: true,
          enableForParent: false,
          enableForEnquiry: false,
        });
        setSuccessMessage("iLearn portlet added successfully");
      } else {
        setError(response.error || "Failed to add iLearn portlet");
      }
    } catch (err) {
      setError("Failed to add iLearn portlet");
    }
  };

  const handleILearnDragStart = (portletId: string) => {
    setDraggedPortlet(portletId);
  };

  const handleILearnDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
  };

  const handleILearnDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedPortlet || draggedPortlet === targetId) return;

    const fromIndex = ilearnPortlets.findIndex((p) => p.id === draggedPortlet);
    const toIndex = ilearnPortlets.findIndex((p) => p.id === targetId);
    if (fromIndex === -1 || toIndex === -1) return;

    const reordered = [...ilearnPortlets];
    const [removed] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, removed);

    setIlearnPortlets(reordered.map((p, index) => ({ ...p, order: index })));
    setDraggedPortlet(null);
  };

  // iLearn JSON edit handlers
  const handleILearnJsonSave = async () => {
    try {
      const parsed = JSON.parse(jsonText) as ILearnPortlet;
      if (!parsed.id || !parsed.title) {
        setJsonError("Invalid portlet: missing required fields (id, title)");
        return;
      }
      setSelectedIlearnPortlet(parsed);
      const updatedPortlets = ilearnPortlets.map((p) => (p.id === parsed.id ? parsed : p));
      setIlearnPortlets(updatedPortlets);
      setJsonError(null);
      setJsonEditMode(false);

      setSaving(true);
      try {
        const response = await PortletConfigService.updateILearnPortletConfig(updatedPortlets);
        if (response.success) {
          setSuccessMessage("iLearn configuration saved successfully");
        } else {
          setError(response.error || "Failed to save iLearn configuration");
        }
      } finally {
        setSaving(false);
      }
    } catch (e) {
      setJsonError("Invalid JSON format");
    }
  };

  const toggleILearnJsonMode = () => {
    if (!jsonEditMode && selectedIlearnPortlet) {
      setJsonText(JSON.stringify(selectedIlearnPortlet, null, 2));
      setJsonError(null);
    }
    setJsonEditMode(!jsonEditMode);
  };

  // Group portlets by category
  const groupedPortlets = portlets.reduce((acc, portlet) => {
    const category = portlet.category || "Uncategorized";
    if (!acc[category]) acc[category] = [];
    acc[category].push(portlet);
    return acc;
  }, {} as Record<string, Portlet[]>);

  const groupedIlearnPortlets = ilearnPortlets.reduce((acc, portlet) => {
    const category = portlet.category || "Uncategorized";
    if (!acc[category]) acc[category] = [];
    acc[category].push(portlet);
    return acc;
  }, {} as Record<string, ILearnPortlet[]>);

  if (loading) {
    return (
      <div className="portlet-config">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading portlet configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="portlet-config">
      {/* Header */}
      <header className="config-header">
        <div>
          <h1>Portlet Configuration</h1>
          <p className="header-subtitle">
            Manage dashboard portlets, add custom links, and configure visibility
          </p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="btn-add"
            style={{
              background: "var(--surface-card)",
              color: "var(--brand-primary)",
              border: "1px solid var(--brand-primary-subtle)",
            }}
            onClick={() => history.push("/tenant-preferences")}
            title="Tenant Preferences — branding, receipts, theme"
          >
            ⚙ Tenant Preferences
          </button>
          <button
            className="btn-add"
            onClick={() => setShowAddModal(true)}
          >
            + Add Portlet
          </button>
          <button
            className="btn-save"
            onClick={activeTab === "iteach" ? handleSave : handleILearnSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </header>

      {/* Tab Bar */}
      <div className="tab-bar">
        <button
          className={`tab-btn ${activeTab === "iteach" ? "active" : ""}`}
          onClick={() => { setActiveTab("iteach"); setSelectedIlearnPortlet(null); setJsonEditMode(false); }}
        >
          iTeach Portlets
        </button>
        <button
          className={`tab-btn ${activeTab === "ilearn" ? "active" : ""}`}
          onClick={() => { setActiveTab("ilearn"); setSelectedPortlet(null); setJsonEditMode(false); }}
        >
          iLearn Portlets
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>&times;</button>
        </div>
      )}

      {/* Main Content */}
      {activeTab === "iteach" && (
      <div className="config-content">
        {/* Portlet Grid */}
        <div className="portlet-grid-container">
          {Object.entries(groupedPortlets).map(([category, categoryPortlets]) => (
            <div key={category} className="category-section">
              <h2 className="category-title">{category}</h2>
              <div className="portlet-grid">
                {categoryPortlets
                  .sort((a, b) => a.order - b.order)
                  .map((portlet) => (
                    <div
                      key={portlet.id}
                      className={`portlet-card ${selectedPortlet?.id === portlet.id ? "selected" : ""} ${!portlet.visible ? "hidden" : ""} ${draggedPortlet === portlet.id ? "dragging" : ""}`}
                      style={{ background: portlet.gradient || "#f8fafc" }}
                      draggable
                      onDragStart={() => handleDragStart(portlet.id)}
                      onDragOver={(e) => handleDragOver(e, portlet.id)}
                      onDrop={(e) => handleDrop(e, portlet.id)}
                      onClick={() => setSelectedPortlet(portlet)}
                    >
                      <div className="portlet-icon">
                        <IconDisplay icon={portlet.icon} size={28} />
                      </div>
                      <div className="portlet-info">
                        <h3>{portlet.title}</h3>
                        <span className="portlet-type">{portlet.type}</span>
                      </div>
                      <div className="portlet-actions">
                        <button
                          className={`btn-visibility ${portlet.visible ? "visible" : "hidden"}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleVisibility(portlet.id);
                          }}
                          title={portlet.visible ? "Hide" : "Show"}
                        >
                          {portlet.visible ? "👁" : "👁‍🗨"}
                        </button>
                      </div>
                    </div>
                  ))}
                {/* Add button at end of category */}
                <div
                  className="portlet-card add-card"
                  onClick={() => {
                    setNewPortlet((prev) => ({ ...prev, category }));
                    setShowAddModal(true);
                  }}
                >
                  <div className="add-icon">+</div>
                  <span>Add to {category}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Detail Panel */}
        {selectedPortlet && (
          <div className="detail-panel">
            <div className="panel-header">
              <h3>Edit Portlet</h3>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <button
                  className={`btn-mode-toggle ${jsonEditMode ? "active" : ""}`}
                  onClick={toggleJsonMode}
                  title={jsonEditMode ? "Switch to Form" : "Switch to JSON"}
                >
                  {jsonEditMode ? "Form" : "JSON"}
                </button>
                <button
                  className="btn-close"
                  onClick={() => {
                    setSelectedPortlet(null);
                    setJsonEditMode(false);
                  }}
                >
                  &times;
                </button>
              </div>
            </div>

            <div className="panel-body">
              {jsonEditMode ? (
                <>
                  <div className="form-group">
                    <label>JSON Configuration</label>
                    <textarea
                      className="json-editor"
                      value={jsonText}
                      onChange={(e) => {
                        setJsonText(e.target.value);
                        setJsonError(null);
                      }}
                      rows={16}
                      spellCheck={false}
                    />
                    {jsonError && (
                      <div className="json-error">{jsonError}</div>
                    )}
                  </div>
                  <div className="panel-actions">
                    <button className="btn-json-save" onClick={handleJsonSave}>
                      Apply JSON
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={selectedPortlet.title}
                      onChange={(e) => handleUpdatePortlet("title", e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={selectedPortlet.description}
                      onChange={(e) => handleUpdatePortlet("description", e.target.value)}
                      rows={2}
                    />
                  </div>

                  <div className="form-group">
                    <label>Icon</label>
                    <div className="emoji-picker">
                      {EMOJI_OPTIONS.map((emoji) => (
                        <button
                          key={emoji}
                          className={`emoji-btn ${selectedPortlet.icon.type === "emoji" && selectedPortlet.icon.value === emoji ? "selected" : ""}`}
                          onClick={() => handleUpdatePortlet("icon", { type: "emoji", value: emoji })}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                    <LucideBrowseRow icon={selectedPortlet.icon} target="iteachEdit" />
                    <IconColorPicker
                      icon={selectedPortlet.icon}
                      onChange={(color) => handleUpdatePortlet("icon", withIconColor(selectedPortlet.icon, color))}
                    />
                  </div>

                  <div className="form-group">
                    <label>Gradient</label>
                    <div className="gradient-picker">
                      {GRADIENT_OPTIONS.map((grad) => (
                        <button
                          key={grad.value}
                          className={`gradient-btn ${selectedPortlet.gradient === grad.value ? "selected" : ""}`}
                          style={{ background: grad.value }}
                          onClick={() => handleUpdatePortlet("gradient", grad.value)}
                          title={grad.label}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Category</label>
                    <input
                      type="text"
                      value={selectedPortlet.category}
                      onChange={(e) => handleUpdatePortlet("category", e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Role Access
                      <span className="role-hint">empty = visible to all</span>
                    </label>
                    <RoleSelector
                      roleIds={selectedPortlet.roleIds || []}
                      onChange={(ids) => handleUpdatePortlet("roleIds", ids)}
                      availableRoles={availableRoles}
                    />
                  </div>

                  {selectedPortlet.type === "link" && (
                    <div className="form-group">
                      <label>URL</label>
                      <input
                        type="text"
                        value={selectedPortlet.url || ""}
                        onChange={(e) => handleUpdatePortlet("url", e.target.value)}
                        placeholder="https://..."
                      />
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedPortlet.openInNewTab || false}
                          onChange={(e) => handleUpdatePortlet("openInNewTab", e.target.checked)}
                        />
                        Open in new tab
                      </label>
                    </div>
                  )}

                  {selectedPortlet.type === "standard" && (
                    <div className="form-group">
                      <label>Route Path</label>
                      <input
                        type="text"
                        value={selectedPortlet.route?.path || ""}
                        onChange={(e) => handleUpdatePortlet("route", { path: e.target.value })}
                        placeholder="/dashboard"
                      />
                    </div>
                  )}

                  {selectedPortlet.type === "group" && (
                    <div className="form-group">
                      <label>Children</label>
                      <ChildrenEditor
                        children={selectedPortlet.children || []}
                        onChange={(c) => handleUpdatePortlet("children" as any, c)}
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedPortlet.visible}
                        onChange={(e) => handleUpdatePortlet("visible", e.target.checked)}
                      />
                      Visible to users
                    </label>
                  </div>

                  <div className="panel-actions">
                    <button
                      className="btn-delete"
                      onClick={() => handleDeletePortlet(selectedPortlet.id)}
                    >
                      Delete Portlet
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {/* iLearn Content */}
      {activeTab === "ilearn" && (
        <div className="config-content">
          {ilearnLoading ? (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading iLearn portlet configuration...</p>
            </div>
          ) : (
            <>
              {/* iLearn Portlet Grid */}
              <div className="portlet-grid-container">
                {Object.entries(groupedIlearnPortlets).map(([category, categoryPortlets]) => (
                  <div key={category} className="category-section">
                    <h2 className="category-title">{category}</h2>
                    <div className="portlet-grid">
                      {categoryPortlets
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((portlet) => (
                          <div
                            key={portlet.id}
                            className={`portlet-card ${selectedIlearnPortlet?.id === portlet.id ? "selected" : ""} ${!portlet.visible ? "hidden" : ""} ${draggedPortlet === portlet.id ? "dragging" : ""}`}
                            style={{ background: portlet.gradient || "#f8fafc" }}
                            draggable
                            onDragStart={() => handleILearnDragStart(portlet.id)}
                            onDragOver={(e) => handleILearnDragOver(e, portlet.id)}
                            onDrop={(e) => handleILearnDrop(e, portlet.id)}
                            onClick={() => { setSelectedIlearnPortlet(portlet); setJsonEditMode(false); }}
                          >
                            <div className="portlet-icon">
                              <IconDisplay icon={portlet.icon} size={28} />
                            </div>
                            <div className="portlet-info">
                              <h3>{portlet.title}</h3>
                              <span className="portlet-type">{portlet.type || "standard"}</span>
                            </div>
                            <div className="portlet-actions">
                              <button
                                className={`btn-visibility ${portlet.visible ? "visible" : "hidden"}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleILearnToggleVisibility(portlet.id);
                                }}
                                title={portlet.visible ? "Hide" : "Show"}
                              >
                                {portlet.visible ? "👁" : "👁‍🗨"}
                              </button>
                            </div>
                          </div>
                        ))}
                      <div
                        className="portlet-card add-card"
                        onClick={() => {
                          setNewIlearnPortlet((prev) => ({ ...prev, category }));
                          setShowAddModal(true);
                        }}
                      >
                        <div className="add-icon">+</div>
                        <span>Add to {category}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* iLearn Detail Panel */}
              {selectedIlearnPortlet && (
                <div className="detail-panel">
                  <div className="panel-header">
                    <h3>Edit iLearn Portlet</h3>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <button
                        className={`btn-mode-toggle ${jsonEditMode ? "active" : ""}`}
                        onClick={toggleILearnJsonMode}
                        title={jsonEditMode ? "Switch to Form" : "Switch to JSON"}
                      >
                        {jsonEditMode ? "Form" : "JSON"}
                      </button>
                      <button
                        className="btn-close"
                        onClick={() => { setSelectedIlearnPortlet(null); setJsonEditMode(false); }}
                      >
                        &times;
                      </button>
                    </div>
                  </div>

                  <div className="panel-body">
                    {jsonEditMode ? (
                      <>
                        <div className="form-group">
                          <label>JSON Configuration</label>
                          <textarea
                            className="json-editor"
                            value={jsonText}
                            onChange={(e) => { setJsonText(e.target.value); setJsonError(null); }}
                            rows={16}
                            spellCheck={false}
                          />
                          {jsonError && <div className="json-error">{jsonError}</div>}
                        </div>
                        <div className="panel-actions">
                          <button className="btn-json-save" onClick={handleILearnJsonSave}>
                            Apply JSON
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="form-group">
                          <label>Title</label>
                          <input
                            type="text"
                            value={selectedIlearnPortlet.title}
                            onChange={(e) => handleILearnUpdatePortlet("title", e.target.value)}
                          />
                        </div>

                        <div className="form-group">
                          <label>Description</label>
                          <textarea
                            value={selectedIlearnPortlet.description}
                            onChange={(e) => handleILearnUpdatePortlet("description", e.target.value)}
                            rows={2}
                          />
                        </div>

                        <div className="form-group">
                          <label>Icon</label>
                          {(() => {
                            const icon = selectedIlearnPortlet.icon;
                            const iconMode: "image" | "key" | "emoji" =
                              typeof icon === "string"
                                ? "image"
                                : icon?.type === "key"
                                ? "key"
                                : icon?.type === "url"
                                ? "image"
                                : "emoji";
                            return (
                              <>
                                <div style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 12 }}>
                                  <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                                    <input
                                      type="radio"
                                      checked={iconMode === "emoji"}
                                      onChange={() =>
                                        handleILearnUpdatePortlet("icon", {
                                          type: "emoji",
                                          value: typeof icon !== "string" ? icon?.value || "📄" : "📄",
                                        })
                                      }
                                    />
                                    Emoji
                                  </label>
                                  <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                                    <input
                                      type="radio"
                                      checked={iconMode === "key"}
                                      onChange={() =>
                                        handleILearnUpdatePortlet("icon", {
                                          type: "key",
                                          key: typeof icon !== "string" ? icon?.key || "" : "",
                                        })
                                      }
                                    />
                                    Icon Key
                                  </label>
                                  <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                                    <input
                                      type="radio"
                                      checked={iconMode === "image"}
                                      onChange={() => {
                                        const existing =
                                          typeof icon === "string"
                                            ? icon
                                            : icon?.type === "url"
                                            ? icon.value || ""
                                            : "";
                                        handleILearnUpdatePortlet("icon", existing);
                                      }}
                                    />
                                    Image URL
                                  </label>
                                </div>
                                {iconMode === "key" && typeof icon !== "string" ? (
                                  <input
                                    type="text"
                                    value={icon.key || ""}
                                    onChange={(e) =>
                                      handleILearnUpdatePortlet("icon", { type: "key", key: e.target.value })
                                    }
                                    placeholder="e.g. PROFILE, ONLINE_TESTS, ANNOUNCEMENTS"
                                  />
                                ) : iconMode === "image" ? (
                                  <input
                                    type="text"
                                    value={typeof icon === "string" ? icon : ""}
                                    onChange={(e) => handleILearnUpdatePortlet("icon", e.target.value)}
                                    placeholder="./assets/imgs/learningmodules.png  or  https://..."
                                  />
                                ) : (
                                  <>
                                    <div className="emoji-picker">
                                      {EMOJI_OPTIONS.map((emoji) => (
                                        <button
                                          key={emoji}
                                          className={`emoji-btn ${
                                            typeof icon !== "string" && icon?.type === "emoji" && icon.value === emoji
                                              ? "selected"
                                              : ""
                                          }`}
                                          onClick={() => handleILearnUpdatePortlet("icon", { type: "emoji", value: emoji })}
                                        >
                                          {emoji}
                                        </button>
                                      ))}
                                    </div>
                                    <LucideBrowseRow icon={icon} target="ilearnEdit" />
                                    <IconColorPicker
                                      icon={icon}
                                      onChange={(color) => handleILearnUpdatePortlet("icon", withIconColor(icon, color))}
                                    />
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        <div className="form-group">
                          <label>Gradient</label>
                          <div className="gradient-picker">
                            {GRADIENT_OPTIONS.map((grad) => (
                              <button
                                key={grad.value}
                                className={`gradient-btn ${selectedIlearnPortlet.gradient === grad.value ? "selected" : ""}`}
                                style={{ background: grad.value }}
                                onClick={() => handleILearnUpdatePortlet("gradient", grad.value)}
                                title={grad.label}
                              />
                            ))}
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Category</label>
                          <input
                            type="text"
                            value={selectedIlearnPortlet.category}
                            onChange={(e) => handleILearnUpdatePortlet("category", e.target.value)}
                          />
                        </div>

                        {/* Route / URL based on type */}
                        <div className="form-group">
                          <label>Type</label>
                          <select
                            value={selectedIlearnPortlet.type || "standard"}
                            onChange={(e) => handleILearnUpdatePortlet("type", e.target.value)}
                          >
                            <option value="standard">Standard (Route)</option>
                            <option value="link">Link (URL)</option>
                          </select>
                        </div>

                        {(selectedIlearnPortlet.type || "standard") === "standard" && (
                          <>
                            <div className="form-group">
                              <label>Route Path</label>
                              <input
                                type="text"
                                value={selectedIlearnPortlet.route?.path || ""}
                                onChange={(e) => handleILearnUpdatePortlet("route", { path: e.target.value })}
                                placeholder="/TestList"
                              />
                            </div>
                            <div className="form-group">
                              <label>
                                Params
                                <span className="role-hint">
                                  e.g. resourceTypeId, resourceSubTypeId, testCategoryIds
                                </span>
                              </label>
                              <ParamsEditor
                                key={selectedIlearnPortlet.id}
                                params={selectedIlearnPortlet.params}
                                onChange={(next) => handleILearnUpdatePortlet("params", next)}
                              />
                            </div>
                          </>
                        )}

                        {selectedIlearnPortlet.type === "link" && (
                          <div className="form-group">
                            <label>URL</label>
                            <input
                              type="text"
                              value={selectedIlearnPortlet.url || ""}
                              onChange={(e) => handleILearnUpdatePortlet("url", e.target.value)}
                              placeholder="https://..."
                            />
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={selectedIlearnPortlet.openInNewTab || false}
                                onChange={(e) => handleILearnUpdatePortlet("openInNewTab", e.target.checked)}
                              />
                              Open in new tab
                            </label>
                          </div>
                        )}

                        <div className="form-group">
                          <label>
                            Role Access
                            <span className="role-hint">empty = visible to all</span>
                          </label>
                          <RoleSelector
                            roleIds={selectedIlearnPortlet.roleIds || []}
                            onChange={(ids) => handleILearnUpdatePortlet("roleIds", ids)}
                            availableRoles={availableRoles}
                          />
                        </div>

                        {/* iLearn visibility toggles */}
                        <div className="form-group">
                          <label>Visibility Settings</label>
                          <div className="toggle-section">
                            <div className="toggle-row">
                              <span className="toggle-label">Show as Dashboard Tile</span>
                              <input
                                type="checkbox"
                                checked={selectedIlearnPortlet.isTile !== false}
                                onChange={(e) => handleILearnUpdatePortlet("isTile", e.target.checked)}
                              />
                            </div>
                            <div className="toggle-row">
                              <span className="toggle-label">Show in Sidebar Menu</span>
                              <input
                                type="checkbox"
                                checked={selectedIlearnPortlet.isMenu || false}
                                onChange={(e) => handleILearnUpdatePortlet("isMenu", e.target.checked)}
                              />
                            </div>
                            {selectedIlearnPortlet.isMenu && (
                              <div className="form-group" style={{ marginTop: 8 }}>
                                <label>Menu Icon</label>
                                <input
                                  type="text"
                                  value={selectedIlearnPortlet.menuIcon || ""}
                                  onChange={(e) => handleILearnUpdatePortlet("menuIcon", e.target.value)}
                                  placeholder="icon class name"
                                />
                              </div>
                            )}
                            <div className="toggle-row">
                              <span className="toggle-label">Visible to Students</span>
                              <input
                                type="checkbox"
                                checked={selectedIlearnPortlet.enableForEnrollment !== false}
                                onChange={(e) => handleILearnUpdatePortlet("enableForEnrollment", e.target.checked)}
                              />
                            </div>
                            <div className="toggle-row">
                              <span className="toggle-label">Visible to Parents</span>
                              <input
                                type="checkbox"
                                checked={selectedIlearnPortlet.enableForParent || false}
                                onChange={(e) => handleILearnUpdatePortlet("enableForParent", e.target.checked)}
                              />
                            </div>
                            <div className="toggle-row">
                              <span className="toggle-label">Visible to Enquiry Users</span>
                              <input
                                type="checkbox"
                                checked={selectedIlearnPortlet.enableForEnquiry || false}
                                onChange={(e) => handleILearnUpdatePortlet("enableForEnquiry", e.target.checked)}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Course/Program IDs (comma-separated)</label>
                          <input
                            type="text"
                            value={(selectedIlearnPortlet.courseOrProgramIds || []).join(", ")}
                            onChange={(e) => {
                              const ids = e.target.value
                                .split(",")
                                .map((s) => s.trim())
                                .filter((s) => s && !isNaN(Number(s)))
                                .map(Number);
                              handleILearnUpdatePortlet("courseOrProgramIds", ids);
                            }}
                            placeholder="Leave empty for all"
                          />
                        </div>

                        <div className="form-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={selectedIlearnPortlet.visible}
                              onChange={(e) => handleILearnUpdatePortlet("visible", e.target.checked)}
                            />
                            Visible (master toggle)
                          </label>
                        </div>

                        <div className="panel-actions">
                          <button
                            className="btn-delete"
                            onClick={() => setShowDeleteConfirm(selectedIlearnPortlet.id)}
                          >
                            Delete Portlet
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" role="presentation" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button className="btn-close" onClick={() => setShowDeleteConfirm(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this portlet? This action cannot be undone.</p>
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button className="btn-cancel" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
                <button className="btn-delete" onClick={() => handleILearnDeletePortlet(showDeleteConfirm)}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Utilities */}
      <div style={{ marginTop: 32, padding: "20px 0", borderTop: "1px solid var(--border-default)" }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: "var(--text-body)", marginBottom: 12 }}>
          System Utilities
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            className="btn-save"
            onClick={handleRefreshPlaygroundCache}
            disabled={refreshingCache}
            style={{ minWidth: 200 }}
          >
            {refreshingCache ? "Rebuilding..." : "Refresh Playground Cache"}
          </button>
          {cacheMessage && (
            <span style={{ fontSize: 13, color: cacheMessage.startsWith("Failed") ? "#ef4444" : "#059669" }}>
              {cacheMessage}
            </span>
          )}
        </div>
        <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
          Rebuilds the tag tree and question counts shown in the student Playground. Run after adding or modifying questions.
        </p>
        <div style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <a
            href="/ai-workspace-config?mode=global-doubt"
            className="btn-save"
            style={{ minWidth: 200, display: "inline-block", textAlign: "center", textDecoration: "none" }}
          >
            AI Doubt Settings
          </a>
          <a
            href="/ai-workspace-config?mode=global-generic"
            className="btn-save"
            style={{ minWidth: 200, display: "inline-block", textAlign: "center", textDecoration: "none" }}
          >
            AI Assistant Settings
          </a>
          <a
            href="/ai-workspace-config?mode=global-resource-default"
            className="btn-save"
            style={{ minWidth: 200, display: "inline-block", textAlign: "center", textDecoration: "none" }}
          >
            Default AI Workspace
          </a>
        </div>
        <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
          Configure AI settings: &quot;Doubt&quot; for Ask-a-Doubt, &quot;Assistant&quot; for generic AI on learning modules, &quot;Default Workspace&quot; for resources without their own config.
        </p>
      </div>

      {/* Add Portlet Modal */}
      {showAddModal && (
        <div className="modal-overlay" role="presentation" onClick={() => { setShowAddModal(false); setAddPortletType(null); }}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="add-portlet-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 id="add-portlet-title">Add New {activeTab === "ilearn" ? "iLearn" : ""} Portlet</h2>
              <button className="btn-close" onClick={() => { setShowAddModal(false); setAddPortletType(null); }}>
                &times;
              </button>
            </div>

            <div className="modal-body">
              {/* iLearn Add Form */}
              {activeTab === "ilearn" ? (
                !addPortletType ? (
                  <div className="type-selector">
                    <p>What type of portlet would you like to add?</p>
                    <div className="type-options">
                      <button
                        className="type-option"
                        onClick={() => {
                          setAddPortletType("link");
                          setNewIlearnPortlet((prev) => ({ ...prev, type: "standard" }));
                        }}
                      >
                        <span className="type-icon">📄</span>
                        <span className="type-name">Standard</span>
                        <span className="type-desc">Route-based iLearn page</span>
                      </button>
                      <button
                        className="type-option"
                        onClick={() => {
                          setAddPortletType("link");
                          setNewIlearnPortlet((prev) => ({ ...prev, type: "link" }));
                        }}
                      >
                        <span className="type-icon">🔗</span>
                        <span className="type-name">Link</span>
                        <span className="type-desc">External or internal URL</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="portlet-form">
                    <div className="form-group">
                      <label>Title *</label>
                      <input
                        type="text"
                        value={newIlearnPortlet.title || ""}
                        onChange={(e) => setNewIlearnPortlet((prev) => ({ ...prev, title: e.target.value }))}
                        placeholder="My Portlet"
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={newIlearnPortlet.description || ""}
                        onChange={(e) => setNewIlearnPortlet((prev) => ({ ...prev, description: e.target.value }))}
                        rows={2}
                        placeholder="Brief description..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Icon</label>
                      {(() => {
                        const icon = newIlearnPortlet.icon;
                        const iconMode: "image" | "key" | "emoji" =
                          typeof icon === "string"
                            ? "image"
                            : icon?.type === "key"
                            ? "key"
                            : icon?.type === "url"
                            ? "image"
                            : "emoji";
                        return (
                          <>
                            <div style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 12 }}>
                              <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                                <input
                                  type="radio"
                                  checked={iconMode === "emoji"}
                                  onChange={() =>
                                    setNewIlearnPortlet((prev) => ({
                                      ...prev,
                                      icon: {
                                        type: "emoji",
                                        value: typeof prev.icon !== "string" ? prev.icon?.value || "📄" : "📄",
                                      },
                                    }))
                                  }
                                />
                                Emoji
                              </label>
                              <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                                <input
                                  type="radio"
                                  checked={iconMode === "key"}
                                  onChange={() =>
                                    setNewIlearnPortlet((prev) => ({
                                      ...prev,
                                      icon: {
                                        type: "key",
                                        key: typeof prev.icon !== "string" ? prev.icon?.key || "" : "",
                                      },
                                    }))
                                  }
                                />
                                Icon Key
                              </label>
                              <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                                <input
                                  type="radio"
                                  checked={iconMode === "image"}
                                  onChange={() =>
                                    setNewIlearnPortlet((prev) => {
                                      const existing =
                                        typeof prev.icon === "string"
                                          ? prev.icon
                                          : prev.icon?.type === "url"
                                          ? prev.icon.value || ""
                                          : "";
                                      return { ...prev, icon: existing };
                                    })
                                  }
                                />
                                Image URL
                              </label>
                            </div>
                            {iconMode === "key" && typeof icon !== "string" ? (
                              <input
                                type="text"
                                value={icon.key || ""}
                                onChange={(e) =>
                                  setNewIlearnPortlet((prev) => ({
                                    ...prev,
                                    icon: { type: "key", key: e.target.value },
                                  }))
                                }
                                placeholder="e.g. PROFILE, ONLINE_TESTS, ANNOUNCEMENTS"
                              />
                            ) : iconMode === "image" ? (
                              <input
                                type="text"
                                value={typeof icon === "string" ? icon : ""}
                                onChange={(e) =>
                                  setNewIlearnPortlet((prev) => ({ ...prev, icon: e.target.value }))
                                }
                                placeholder="./assets/imgs/learningmodules.png  or  https://..."
                              />
                            ) : (
                              <>
                                <div className="emoji-picker">
                                  {EMOJI_OPTIONS.map((emoji) => (
                                    <button
                                      key={emoji}
                                      type="button"
                                      className={`emoji-btn ${
                                        typeof icon !== "string" && icon?.type === "emoji" && icon.value === emoji
                                          ? "selected"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        setNewIlearnPortlet((prev) => ({
                                          ...prev,
                                          icon: { type: "emoji", value: emoji },
                                        }))
                                      }
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                                <LucideBrowseRow icon={icon} target="ilearnAdd" />
                                <IconColorPicker
                                  icon={icon}
                                  onChange={(color) =>
                                    setNewIlearnPortlet((prev) => ({ ...prev, icon: withIconColor(prev.icon, color) }))
                                  }
                                />
                              </>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="form-group">
                      <label>Gradient</label>
                      <div className="gradient-picker">
                        {GRADIENT_OPTIONS.map((grad) => (
                          <button
                            key={grad.value}
                            type="button"
                            className={`gradient-btn ${newIlearnPortlet.gradient === grad.value ? "selected" : ""}`}
                            style={{ background: grad.value }}
                            onClick={() => setNewIlearnPortlet((prev) => ({ ...prev, gradient: grad.value }))}
                            title={grad.label}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Category</label>
                      <input
                        type="text"
                        value={newIlearnPortlet.category || ""}
                        onChange={(e) => setNewIlearnPortlet((prev) => ({ ...prev, category: e.target.value }))}
                        placeholder="Learn"
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        Role Access
                        <span className="role-hint">empty = visible to all</span>
                      </label>
                      <RoleSelector
                        roleIds={newIlearnPortlet.roleIds || []}
                        onChange={(ids) => setNewIlearnPortlet((prev) => ({ ...prev, roleIds: ids }))}
                        availableRoles={availableRoles}
                      />
                    </div>

                    {(newIlearnPortlet.type || "standard") === "standard" && (
                      <>
                        <div className="form-group">
                          <label>Route Path *</label>
                          <input
                            type="text"
                            value={newIlearnPortlet.route?.path || ""}
                            onChange={(e) => setNewIlearnPortlet((prev) => ({ ...prev, route: { path: e.target.value } }))}
                            placeholder="/TestList"
                          />
                        </div>
                        <div className="form-group">
                          <label>
                            Params
                            <span className="role-hint">
                              e.g. resourceTypeId, resourceSubTypeId, testCategoryIds
                            </span>
                          </label>
                          <ParamsEditor
                            params={newIlearnPortlet.params}
                            onChange={(next) => setNewIlearnPortlet((prev) => ({ ...prev, params: next }))}
                          />
                        </div>
                      </>
                    )}

                    {newIlearnPortlet.type === "link" && (
                      <div className="form-group">
                        <label>URL *</label>
                        <input
                          type="text"
                          value={newIlearnPortlet.url || ""}
                          onChange={(e) => setNewIlearnPortlet((prev) => ({ ...prev, url: e.target.value }))}
                          placeholder="https://example.com"
                        />
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={newIlearnPortlet.openInNewTab || false}
                            onChange={(e) => setNewIlearnPortlet((prev) => ({ ...prev, openInNewTab: e.target.checked }))}
                          />
                          Open in new tab
                        </label>
                      </div>
                    )}

                    <div className="form-group">
                      <label>Visibility</label>
                      <div className="toggle-section">
                        <div className="toggle-row">
                          <span className="toggle-label">Show as Dashboard Tile</span>
                          <input type="checkbox" checked={newIlearnPortlet.isTile !== false} onChange={(e) => setNewIlearnPortlet((prev) => ({ ...prev, isTile: e.target.checked }))} />
                        </div>
                        <div className="toggle-row">
                          <span className="toggle-label">Visible to Students</span>
                          <input type="checkbox" checked={newIlearnPortlet.enableForEnrollment !== false} onChange={(e) => setNewIlearnPortlet((prev) => ({ ...prev, enableForEnrollment: e.target.checked }))} />
                        </div>
                        <div className="toggle-row">
                          <span className="toggle-label">Visible to Parents</span>
                          <input type="checkbox" checked={newIlearnPortlet.enableForParent || false} onChange={(e) => setNewIlearnPortlet((prev) => ({ ...prev, enableForParent: e.target.checked }))} />
                        </div>
                        <div className="toggle-row">
                          <span className="toggle-label">Visible to Enquiry Users</span>
                          <input type="checkbox" checked={newIlearnPortlet.enableForEnquiry || false} onChange={(e) => setNewIlearnPortlet((prev) => ({ ...prev, enableForEnquiry: e.target.checked }))} />
                        </div>
                      </div>
                    </div>

                    <div className="form-actions">
                      <button type="button" className="btn-cancel" onClick={() => setAddPortletType(null)}>Back</button>
                      <button type="button" className="btn-save" onClick={handleILearnAddPortlet} disabled={!newIlearnPortlet.title}>Add Portlet</button>
                    </div>
                  </div>
                )
              ) : !addPortletType ? (
                <div className="type-selector">
                  <p>What type of portlet would you like to add?</p>
                  <div className="type-options">
                    <button
                      className="type-option"
                      onClick={() => {
                        setAddPortletType("link");
                        setNewPortlet((prev) => ({ ...prev, type: "link" }));
                      }}
                    >
                      <span className="type-icon">🔗</span>
                      <span className="type-name">Link</span>
                      <span className="type-desc">External or internal URL</span>
                    </button>
                    <button
                      className="type-option"
                      onClick={() => {
                        setAddPortletType("clone");
                        setNewPortlet((prev) => ({ ...prev, type: "clone" }));
                      }}
                    >
                      <span className="type-icon">📋</span>
                      <span className="type-name">Clone</span>
                      <span className="type-desc">Copy existing portlet</span>
                    </button>
                    <button
                      className="type-option"
                      onClick={() => {
                        setAddPortletType("widget");
                        setNewPortlet((prev) => ({ ...prev, type: "widget" }));
                      }}
                    >
                      <span className="type-icon">📊</span>
                      <span className="type-name">Widget</span>
                      <span className="type-desc">Embed or chart</span>
                    </button>
                    <button
                      className="type-option"
                      onClick={() => {
                        setAddPortletType("group");
                        setNewPortlet((prev) => ({ ...prev, type: "group", children: [] }));
                      }}
                    >
                      <span className="type-icon">📁</span>
                      <span className="type-name">Group</span>
                      <span className="type-desc">Container with sub-items</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="portlet-form">
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      value={newPortlet.title || ""}
                      onChange={(e) => setNewPortlet((prev) => ({ ...prev, title: e.target.value }))}
                      placeholder="My Custom Link"
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={newPortlet.description || ""}
                      onChange={(e) => setNewPortlet((prev) => ({ ...prev, description: e.target.value }))}
                      rows={2}
                      placeholder="Brief description..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Icon</label>
                    <div className="emoji-picker">
                      {EMOJI_OPTIONS.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          className={`emoji-btn ${newPortlet.icon?.type === "emoji" && newPortlet.icon?.value === emoji ? "selected" : ""}`}
                          onClick={() => setNewPortlet((prev) => ({ ...prev, icon: { type: "emoji", value: emoji } }))}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                    <LucideBrowseRow icon={newPortlet.icon} target="iteachAdd" />
                    <IconColorPicker
                      icon={newPortlet.icon}
                      onChange={(color) => setNewPortlet((prev) => ({ ...prev, icon: withIconColor(prev.icon, color) }))}
                    />
                  </div>

                  <div className="form-group">
                    <label>Gradient</label>
                    <div className="gradient-picker">
                      {GRADIENT_OPTIONS.map((grad) => (
                        <button
                          key={grad.value}
                          type="button"
                          className={`gradient-btn ${newPortlet.gradient === grad.value ? "selected" : ""}`}
                          style={{ background: grad.value }}
                          onClick={() => setNewPortlet((prev) => ({ ...prev, gradient: grad.value }))}
                          title={grad.label}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Category</label>
                    <input
                      type="text"
                      value={newPortlet.category || ""}
                      onChange={(e) => setNewPortlet((prev) => ({ ...prev, category: e.target.value }))}
                      placeholder="Custom"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Role Access
                      <span className="role-hint">empty = visible to all</span>
                    </label>
                    <RoleSelector
                      roleIds={newPortlet.roleIds || []}
                      onChange={(ids) => setNewPortlet((prev) => ({ ...prev, roleIds: ids }))}
                      availableRoles={availableRoles}
                    />
                  </div>

                  {addPortletType === "link" && (
                    <div className="form-group">
                      <label>URL *</label>
                      <input
                        type="text"
                        value={newPortlet.url || ""}
                        onChange={(e) => setNewPortlet((prev) => ({ ...prev, url: e.target.value }))}
                        placeholder="https://example.com"
                      />
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={newPortlet.openInNewTab || false}
                          onChange={(e) => setNewPortlet((prev) => ({ ...prev, openInNewTab: e.target.checked }))}
                        />
                        Open in new tab
                      </label>
                    </div>
                  )}

                  {addPortletType === "clone" && (
                    <div className="form-group">
                      <label>Clone from</label>
                      <select
                        value={newPortlet.sourceId || ""}
                        onChange={(e) => setNewPortlet((prev) => ({ ...prev, sourceId: e.target.value }))}
                      >
                        <option value="">Select a portlet...</option>
                        {portlets
                          .filter((p) => p.type === "standard")
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.title}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}

                  {addPortletType === "widget" && (
                    <div className="form-group">
                      <label>Widget Type</label>
                      <select
                        value={newPortlet.widgetType || ""}
                        onChange={(e) => setNewPortlet((prev) => ({ ...prev, widgetType: e.target.value as any }))}
                      >
                        <option value="">Select type...</option>
                        <option value="iframe">Embed (iframe)</option>
                        <option value="counter">Counter</option>
                        <option value="chart">Chart</option>
                        <option value="list">List</option>
                      </select>
                      {newPortlet.widgetType === "iframe" && (
                        <input
                          type="text"
                          value={(newPortlet.widgetConfig as any)?.url || ""}
                          onChange={(e) =>
                            setNewPortlet((prev) => ({
                              ...prev,
                              widgetConfig: { ...prev.widgetConfig, url: e.target.value },
                            }))
                          }
                          placeholder="Embed URL"
                          style={{ marginTop: 8 }}
                        />
                      )}
                    </div>
                  )}

                  {addPortletType === "group" && (
                    <div className="form-group">
                      <label>Children</label>
                      <ChildrenEditor
                        children={newPortlet.children || []}
                        onChange={(c) => setNewPortlet((prev) => ({ ...prev, children: c }))}
                      />
                    </div>
                  )}

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => {
                        setAddPortletType(null);
                      }}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className="btn-save"
                      onClick={handleAddPortlet}
                      disabled={!newPortlet.title}
                    >
                      Add Portlet
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lucide icon picker (lazy) */}
      {lucidePicker && (
        <Suspense fallback={null}>
          <LucideIconPicker
            current={(() => {
              const icon =
                lucidePicker.target === "iteachEdit"
                  ? selectedPortlet?.icon
                  : lucidePicker.target === "iteachAdd"
                  ? newPortlet.icon
                  : lucidePicker.target === "ilearnEdit"
                  ? selectedIlearnPortlet?.icon
                  : newIlearnPortlet.icon;
              return typeof icon !== "string" && icon?.type === "lucide" ? icon.value : undefined;
            })()}
            onSelect={applyLucide}
            onClose={() => setLucidePicker(null)}
          />
        </Suspense>
      )}
    </div>
  );
};

export default PortletConfig;
